import api from "@/api/client";
import { PaginatedResponse } from "@/api/pagination";
import { Service } from "@/api/types";

/**
 * Get a service by its ID
 * @param serviceId - The service ID to fetch
 * @param tenantDb - Tenant database parameter (required)
 * @returns Promise<Service> - Single service object or undefined if not found
 * @throws Error when service ID is not provided
 */
export const getServiceById = async (serviceId: string | number, tenantDb: string): Promise<Service> => {
  if (!serviceId) {
    throw new Error("Service ID is required");
  }
  const params = { id__in: serviceId, db: tenantDb };
  const response = await api.get<PaginatedResponse<Service>>("/services", { params });
  return response.data.results[0];
};

/**
 * Get a list of services with optional filtering
 * @param tenantDb - Tenant database parameter (required)
 * @param params - Optional query parameters for filtering services
 * @returns Promise<Service[]> - Array of service objects
 */
export const getServices = async (
  tenantDb: string,
  params?: Record<string, string | number | boolean>,
): Promise<Service[]> => {
  const queryParams = { page_size:100, ...params, db: tenantDb };
  const response = await api.get<PaginatedResponse<Service>>("/services", { params: queryParams });
  return response.data.results;
};

/**
 * Get service name by ID - optimized for individual service lookup
 * @param serviceId - The service ID to get the name for
 * @param tenantDb - Tenant database parameter (required)
 * @returns Promise<string> - Service name or "Unknown Service" if not found
 */
export const getServiceName = async (
  serviceId: number | null | undefined,
  tenantDb: string,
): Promise<string> => {
  if (!serviceId) {
    return "Unknown Service";
  }

  try {
    const service = await getServiceById(serviceId, tenantDb);
    return service.servicename || "Unknown Service";
  } catch {
    return "Unknown Service";
  }
};
